import { RolodexEntryType } from 'types'
import { v4 as uuidv4 } from 'uuid'

/**
 * Session metrics data structure for a single Kestrel inspection session.
 * Each session is stored as its own Rolodex entry.
 */
export interface InspectionSessionMetrics {
  sessionId: string
  userEmail: string
  timestamp: string
  inspectableSqft: number | null

  sessionDurationMs: number
  activeTimeMs: number

  annotationsCreated: number
  measurementsCreated: number
  reportsGenerated: number

  // Data validation metrics
  dataValidationTotalTimeMs: number
  dataValidationTimeToExhaustMs: number | null
  dataValidationAnnotationCount: number | null

  // Report extension metrics
  reportExtensionTotalTimeMs: number
  reportTextToGenerateMs: number | null
}

/**
 * Data structure for the kestrel-inspection-metrics Rolodex entry.
 */
export type KestrelInspectionMetricsData = InspectionSessionMetrics

interface MetricsStorageConfig {
  rolodexUrl: string
  authToken: string
  kestrelModelId: string
  tags: {
    organization?: string
    site?: string
  }
}

/**
 * Module-scoped config for metrics storage.
 * Stored at init time, used at save time (including during page unload).
 */
let storageConfig: MetricsStorageConfig | null = null

/**
 * Generates a unique entry ID for a session metrics entry.
 * Format: kestrel-metrics-{modelIdWithoutPrefix}-{sessionId}
 * Transforms kestrel-model-* IDs to kestrel-metrics-* IDs.
 * Entry IDs must be less than 75 characters.
 */
function generateSessionEntryId(
  kestrelModelId: string,
  sessionId: string
): string {
  const sessionSuffix = `-${sessionId.slice(0, 8)}`

  // Replace 'kestrel-model-' prefix with 'kestrel-metrics-'
  let metricsId = kestrelModelId
  if (metricsId.startsWith('kestrel-model-')) {
    metricsId = metricsId.replace('kestrel-model-', 'kestrel-metrics-')
  } else {
    // Fallback: add kestrel-metrics- prefix if not a standard kestrel-model- ID
    metricsId = `kestrel-metrics-${metricsId}`
  }

  return `${metricsId}${sessionSuffix}`
}

/**
 * Initializes the metrics storage module for a new session.
 * Stores config in module scope for use at save time.
 * No need to fetch existing data since each session is its own entry.
 */
export function initializeMetricsStorage(
  rolodexUrl: string,
  authToken: string,
  kestrelModelId: string,
  tags: { organization?: string; site?: string }
): void {
  storageConfig = {
    rolodexUrl,
    authToken,
    kestrelModelId,
    tags,
  }
}

/**
 * Saves session metrics to Rolodex as a new entry.
 * Uses fetch with keepalive:true to survive page unload.
 */
export function saveSessionMetricsToRolodex(
  sessionMetrics: InspectionSessionMetrics
): void {
  if (!storageConfig) {
    return
  }

  const { rolodexUrl, authToken, kestrelModelId, tags } = storageConfig
  const entryId = generateSessionEntryId(
    kestrelModelId,
    sessionMetrics.sessionId
  )

  const entryData = {
    id: entryId,
    type: RolodexEntryType.KESTREL_INSPECTION_METRICS,
    data: sessionMetrics,
    tags: {
      [RolodexEntryType.KESTREL_MODEL]: kestrelModelId,
      ...(tags.organization ? { organization: tags.organization } : {}),
      ...(tags.site ? { site: tags.site } : {}),
    },
  }

  const body = JSON.stringify([entryData])
  const url = `${rolodexUrl}/api/v2/entries/`

  fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body,
    keepalive: true,
  }).catch(() => {
    // Silently fail - we can't do much during page unload
  })
}

/**
 * Creates a session metrics object from the current session data.
 */
export function createSessionMetrics(params: {
  userEmail: string
  inspectableSqft: number | null
  sessionDurationMs: number
  activeTimeMs: number
  annotationsCreated: number
  measurementsCreated: number
  reportsGenerated: number
  dataValidationTotalTimeMs: number
  dataValidationTimeToExhaustMs: number | null
  dataValidationAnnotationCount: number | null
  reportExtensionTotalTimeMs: number
  reportTextToGenerateMs: number | null
}): InspectionSessionMetrics {
  return {
    sessionId: uuidv4(),
    timestamp: new Date().toISOString(),
    ...params,
  }
}

/**
 * Cleans up module state when session ends.
 */
export function cleanupMetricsStorage(): void {
  storageConfig = null
}
